import type { ComponentType } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { CorePlugin, PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { SelectionContext } from '@atlaskit/editor-common/types/selection';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import type { UserIntentPlugin } from '@atlaskit/editor-plugin-user-intent/user-intent-plugin-type';
import type { RemixFeedbackMetadata } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type {
	TrackAIEventPayloads,
	TrackAIActioned,
	RemixAIResultActionedMetadata,
	RemixResultAction,
	RemixResultAttributes,
	RemixResultRegistrationAttributes,
} from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { ConversationAssistantConfig } from '@atlassian/conversation-assistant/types';
import type { SmartCreationContext } from '@atlassian/conversation-assistant/ui/staging-area-modal/SmartCreationContext';

import type { RemixHiddenOptionKind } from '../../types';
import type { RemixOptionKind, RemixOptionSelectionParameters } from '../remix/remix-overlay';
import type { ContentClassification } from './utils/content-classification';
export type { ContentClassification } from './utils/content-classification';
export type { EditorRemixLifecycleArtifactType } from './observability';

export type EditorApiWithCorePlugin = PublicPluginAPI<[CorePlugin, UserIntentPlugin]>;

/** The retained machine session that owns the editor Remix lifecycle projection. */
export type EditorRemixMachineLifecycleSession = Readonly<{
	remixId: string;
	token: symbol;
}>;

/**
 * Product-local ownership callbacks returned when a retained machine session is registered.
 * Terminal authority is latched before the owner releases the machine and released separately
 * after the existing product cleanup has completed.
 */
export type EditorRemixMachineLifecycleOwnershipCleanup = (() => void) & {
	latchTerminalAuthority?: () => void;
	/** Releases the matching terminal authority lease after terminal UX cleanup. */
	settleTerminalAuthority?: () => boolean;
};

/**
 * Settlement reasons shared by the legacy store-driven effects and the machine-owned command
 * effects, so both cohorts report a terminal outcome with the same telemetry and the rollout can be
 * compared across them. They live here rather than in `main.tsx` because the command handlers are
 * mounted in `@atlassian/editor-plugin-malleable-ui`, which cannot import that module.
 */
export const REMIX_REMOTE_ABORT_REASON = 'User stopped from Rovo side chat';
export const DEFAULT_REMIX_ERROR_MESSAGE = 'Rovo agent error during remix';

/**
 * A terminal outcome for the Remix experience trackers.
 *
 * An abort carries the flow it settles, because the component holds per-flow state the reason alone
 * cannot be matched against: its own recorded cancel intent, and whether the flow already handed off
 * to Rovo Chat. It cannot read that identity off the lifecycle session instead, since a flow that
 * ended releases the session before a late command arrives.
 */
export type EditorRemixExperienceOutcome =
	| { error: Error; kind: 'failed' }
	| { kind: 'aborted'; reason: string; remixId: string };

/**
 * Whether the component settled the outcome a handler asked it to settle. `ignored` means this flow
 * already committed a different terminal outcome, so the handler's remaining teardown does not apply
 * either.
 */
export type EditorRemixExperienceSettlement = 'ignored' | 'settled';

/**
 * The editor UX that command handlers drive but cannot reach, because these closures live in
 * `RemixContentComponentInner` and the handlers are mounted above it on a host that outlives
 * terminal cleanup.
 *
 * Intent-shaped rather than raw closures: a handler describes the outcome it wants instead of
 * holding the experience tracker or the preview API, so this seam does not take on their types and a
 * handler cannot settle half of one.
 *
 * Structurally identical to the plugin-side `EditorRemixUxHandles` this is registered into.
 * `@atlassian/editor-plugin-malleable-ui` owns the registry and cannot be imported from here, the
 * same way {@link EditorRemixMachineLifecycleSession} mirrors its session type.
 */
export type EditorRemixUxHandles = {
	/** Records explicit thinking-bar cancellation before request and presentation cleanup. */
	cancelFromThinkingBar?: (cleanup?: () => void) => void;
	/** Fully closes the remix modal, unmounting the content component. */
	closeModal: () => void;
	/**
	 * Closes the ephemeral preview where this cohort's terminal cleanup owns that, and is a no-op
	 * where it does not — in edit mode with in-editor loading on, matching the legacy error path. The
	 * caller decides *that* the preview should close for a terminal outcome; this component decides
	 * whether this cohort closes it.
	 */
	closeTerminalPreview: () => void;
	/**
	 * Marks the result ready and starts the local completion animation. Must not write ProseMirror
	 * lifecycle: the mirror already owns completion off the machine's `ready` phase.
	 */
	markReady: () => void;
	openPreview: () => void;
	/** Opens the Rovo side chat, so a failure is visible where the response is. */
	openSidebar: () => void;
	/** Cancellation-only cleanup, for a teardown that no terminal command settled. */
	settleCancellation: (remixId?: string) => void;
	/**
	 * Settles the experience trackers for a terminal outcome. Returns `ignored` when this flow
	 * already committed a different terminal outcome, which the caller must treat as "my remaining
	 * teardown does not apply to this flow".
	 */
	settleExperience: (outcome: EditorRemixExperienceOutcome) => EditorRemixExperienceSettlement;
	/** `preserveRequest` suppresses only the abort of the in-flight AI request. */
	stopThinking: (options?: { preserveRequest?: boolean; reopenModal?: boolean }) => void;
};

export type EditorRemixLifecycleStartRequest =
	| {
			kind: 'create';
			/** When omitted, the lifecycle machine allocates the conversation identity. */
			conversationId?: string;
			targetNodeId?: string;
	  }
	| {
			kind: 'update';
			conversationId: string;
			targetNodeId: string;
			updateSentinel: string;
			/** Number of conversation messages observed before this update was sent. */
			messageCountBeforeSend?: number;
	  };

export type EditorRemixLifecycleEvent =
	| { type: 'PREVIEW_OPENED' }
	| { type: 'RESULT_READY' }
	| { type: 'ACCEPTED' }
	| { type: 'DIRECT_INSERTION_STARTED' }
	| { type: 'INSERTED' }
	| { type: 'GENERATION_ERROR' }
	| { type: 'GENERATION_ABORTED' }
	| { type: 'INSERTION_FAILED' }
	| { type: 'CANCELLED' };

export type EditorRemixLifecycleEndReason =
	| 'aborted'
	| 'cancelled'
	| 'error'
	| 'handed-off'
	/** Acknowledgement (handoff or insertion) never arrived; outcome unknown, released with no machine event. */
	| 'abandoned';

/**
 * Possession of a handle is the admission decision for this flow. Producers holding one are in
 * treatment and must not re-evaluate rollout controls for the flow's lifetime.
 */
export type EditorRemixLifecycleHandle = {
	conversationId: string;
	remixId: string;
	rollback: () => void;
	/** Returns true only when the machine accepted the event and changed phase. */
	transition?: (event: EditorRemixLifecycleEvent) => boolean;
	end?: (reason: EditorRemixLifecycleEndReason) => void;
	/** Registers product-local ownership only after this exact session is retained. */
	onRetained?: (
		session: EditorRemixMachineLifecycleSession,
	) => EditorRemixMachineLifecycleOwnershipCleanup;
};

export type EditorRemixLifecycleStartOutcome =
	| { status: 'disabled' }
	| { status: 'admitted'; handle: EditorRemixLifecycleHandle }
	| {
			/** Admission was rejected because another Remix flow is already active. */
			status: 'active-flow-rejected';
	  }
	| { status: 'shadow-infrastructure-failure' };

/**
 * The editor's lifecycle vocabulary, expressed without reference to the lifecycle machine.
 *
 * Both sides of the migration are projected into it: the editor's own signals via
 * `deriveLegacyEditorRemixPhase`, and the machine's phase via `deriveMachineEditorRemixPhase`. That
 * is what lets the two be compared without a second normalization of editor state.
 */
export type EditorRemixLifecyclePhase =
	| 'inactive'
	| 'thinking'
	| 'ready'
	| 'previewing'
	| 'inserting'
	| 'error'
	| 'aborted';

/**
 * @private
 * @deprecated Renamed to {@link EditorRemixLifecyclePhase} now that the machine phase projects into
 * the same vocabulary. No in-repo caller remains; retained only so a consumer pinned to an older
 * version of this package keeps compiling, and removable once the editor migration completes.
 */
export type EditorRemixLegacyPhase = EditorRemixLifecyclePhase;

export type EditorRemixLifecycleAdapter = {
	startFlow: (request: EditorRemixLifecycleStartRequest) => EditorRemixLifecycleStartOutcome;
	/**
	 * Reports the editor's own lifecycle phase so the product can compare it against the machine
	 * during migration shadow.
	 *
	 * Strictly observational: implementations must not influence the flow, and the editor ignores
	 * anything returned. Absent when the product does not observe parity, so products without an
	 * adapter emit nothing.
	 */
	observeLegacyPhase?: (legacyPhase: EditorRemixLifecyclePhase) => void;
};

export type InsertedContentIdentity =
	| {
			attribute: 'id' | 'localId';
			value: string;
	  }
	| {
			/** Matches the exact MAUI version on the stable editor node, so an undo is not retained. */
			attribute: 'mauiVersion';
			localId: string;
			value: string;
	  };

/**
 * Only `attributes`, `localId`, the tracked `resultId`, and `elapsedMs` are logged with each event.
 * `previousResultId` and `mediaId` are lookup inputs: `previousResultId` classifies
 * 'reverted' vs 'replaced', and `mediaId` locates the media node (its value is logged as `resultId`).
 */
export type RemixResultTracking = {
	attributes: RemixResultRegistrationAttributes;
	onChange: (action: RemixResultAction, attributes: RemixResultAttributes) => void;
	previousLocalId?: string;
	previousResultId?: string;
	resultId?: string;
} & ({ localId: string; mediaId?: never } | { localId?: never; mediaId: string });

export type RegisterRemixResultTracking = (options: {
	adoptedAnalyticsEvent?: TrackAIEventPayloads;
	shouldDelayActionedEvent?: boolean;
	operation?: 'create' | 'edit';
	previousLocalId?: string;
	previousResultId?: string;
	sourceResultId?: string;
	analyticsEvent: TrackAIActioned & RemixAIResultActionedMetadata;
	identity: InsertedContentIdentity;
	insertedAt?: number;
	interactionSource?: InteractionSource;
	receiverId: string;
}) => void;

export type DynamicUiSource = {
	contentId?: string;
	contentType: string;
	endIndex: number;
	endLocalId: string;
	fragmentAdf: JSONNode[] | null;
	fullAdf: DocNode;
	startIndex: number;
	startLocalId: string;
};

export type RemixModalMetadataLike = {
	[key: string]: unknown;
	aspectRatio?: string | null;
	imageStyle?: string | null;
	dynamicUiType?: RemixOptionKind;
	dynamicUiSubtype?: string;
	interactionSource?: InteractionSource;
	userPrompt?: string;
	onCancel?: () => void;
	aiFeatureName?: string;
	/**
	 * Anchor snapshot captured at click time by the entry point that opened this Remix flow. When
	 * `cc-ce-maui-remix-menu-multivariant` is enabled, this is forwarded to `RemixContainer` (the
	 * single convergence point for all Remix entry points) to render an anchored popup instead of
	 * the centered modal.
	 */
	anchorRect?: DOMRect;
	/** Popup placement to use alongside `anchorRect`. Defaults to `'bottom-start'` when omitted. */
	popupPlacement?: 'bottom-start' | 'bottom-end' | 'right-start';
	/**
	 * When set, the chooser skips its own picker UI and immediately starts the
	 * remix flow for this option type. Pairs with `preselectedSubType`.
	 */
	preselectedType?: RemixOptionKind;
	preselectedSubType?: string;
	/** Complete prompt submitted from an external Remix picker. */
	preselectedUserPrompt?: string;
	/** Conversation created before the native editor Remix flow starts. */
	preselectedConversationId?: string;
};

export type AddRemixHistoryEntryPayload = {
	/** Invocation id of the result acknowledged into the editor for the next update baseline. */
	admissionBaselineInvocationId?: string;
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	dynamicUiSubtype?: string;
	dynamicUiType: string;
	finalMessage: StoreAssistant;
	/** Classification captured from ACTION_CLASSIFICATION plus any content-specific stream data. */
	contentClassification?: ContentClassification;
	/** for image, there should be image fieldId */
	fieldId?: string;
	/** for MAUI extensions, there should be MAUI url */
	url?: string;
};

export type RemixHistoryEntry = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	latestFinalMessage: StoreAssistant;
	latestFinalMessageId?: string;
	stack: Array<{
		/** Invocation id of the acknowledged result represented by this stack entry. */
		admissionBaselineInvocationId?: string;
		dynamicUiSubtype?: string;
		dynamicUiType: string;
		fieldId?: string;
		finalMessage: StoreAssistant;
		finalMessageId?: string;
		url?: string;
	}>;
};

export type RemixHistoryEntryForUpdate = {
	/** Invocation id of the displayed artifact selected from the history stack. */
	admissionBaselineInvocationId?: string;
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	latestFinalMessage?: StoreAssistant;
	latestFinalMessageId?: string;
	dynamicUiSubtype?: string;
	dynamicUiType: string;
	fieldId?: string;
	finalMessage: StoreAssistant;
	finalMessageId?: string;
	url?: string;
};

export type UpdateRemixImageHistoryFieldIdPayload = {
	conversationId: string;
	fieldId: string;
};

export type AddRemixHistoryStackEntryPayload = {
	/** Invocation id of the acknowledged replacement represented by this entry. */
	admissionBaselineInvocationId?: string;
	conversationId: string;
	dynamicUiType: string;
	dynamicUiSubtype?: string;
	fieldId?: string;
	finalMessage: StoreAssistant;
	previousFieldId?: string;
	previousUrl?: string;
	url?: string;
};

/**
 * Dynamic-ui (MAUI) variant of {@link AddBackendRemixHistoryStackEntryPayload}.
 * Used when the bridge stream regenerates a MAUI app on the page.
 */
export type AddBackendMauiRemixHistoryStackEntryPayload = {
	/** The MAUI app id being replaced. */
	previousMauiId: string;
	/** New MAUI app URL synthesized from the BE-side new app id. */
	url: string;
	/** Inherits from the matched stack entry when omitted. */
	dynamicUiType?: string;
	/** Backend-confirmed subtype from a type-changing edit; inherited from the matched entry when omitted. */
	dynamicUiSubtype?: string;
};

/**
 * Image variant of {@link AddBackendRemixHistoryStackEntryPayload}.
 * Used when the bridge stream regenerates an image on the page.
 */
export type AddBackendImageRemixHistoryStackEntryPayload = {
	/** The media file id being replaced (BE-side previousMediaFileId). */
	previousFieldId: string;
	/** Live upload-side file id from onUploadAndInsertMedia's result. */
	fieldId: string;
};

/**
 * Payload for appending a stack entry from a backend-initiated remix update
 * (bridge stream). The reducer locates the existing entry by matching the
 * previous identifier against stack entries' MAUI app id (extracted from url)
 * or fieldId, then inherits `finalMessage`, `finalMessageId`, and (when omitted)
 * `dynamicUiType`/`dynamicUiSubtype` from the matched entry.
 *
 * Discriminated union of the dynamic-ui (MAUI) and image variants;
 * narrow with {@link isAddBackendMauiRemixHistoryStackEntryPayload}.
 */
export type AddBackendRemixHistoryStackEntryPayload =
	| AddBackendMauiRemixHistoryStackEntryPayload
	| AddBackendImageRemixHistoryStackEntryPayload;

/**
 * Type guard discriminating the dynamic-ui (MAUI) variant of
 * {@link AddBackendRemixHistoryStackEntryPayload} from the image variant.
 * The two branches have disjoint required fields (`previousMauiId` vs
 * `previousFieldId`), so an `in`-check on either is a safe discriminator.
 */
export function isAddBackendMauiRemixHistoryStackEntryPayload(
	payload: AddBackendRemixHistoryStackEntryPayload,
): payload is AddBackendMauiRemixHistoryStackEntryPayload {
	return 'previousMauiId' in payload;
}

/**
 * Props specific to the remix type.
 */
export type RemixTypeProps =
	| {
			remixType?: 'block';
	  }
	| {
			remixType: 'object';
			adfDocument: DocNode | Promise<DocNode>;
	  };

export type WhiteboardEmbedReplacementOutcome =
	| 'content-id-mismatch'
	| 'editor-unavailable'
	| 'local-id-mismatch'
	| 'node-not-found'
	| 'replaced'
	| 'execution-not-acknowledged'
	| 'unknown';

export type WhiteboardEmbedReplacementResult = {
	acknowledged: boolean;
	outcome: WhiteboardEmbedReplacementOutcome;
};

export type WhiteboardEmbedReplacementCallback = (options: {
	contentId: string;
	finalizedUrl: string;
	localId?: string;
}) => boolean | void | WhiteboardEmbedReplacementResult;

export type RemixContentComponentProps<
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
> = RemixTypeProps & {
	addHistoryEntry?: (options: AddRemixHistoryEntryPayload) => void;
	addHistoryStackEntry?: (options: AddRemixHistoryStackEntryPayload) => void;
	/**
	 * True while a lifecycle-machine session still owns this flow.
	 *
	 * The visibility gate keys off `isThinking`, which the machine projection turns off as soon as
	 * an early streaming whiteboard is placed, while generation continues. Without this the subtree
	 * that finalizes the insertion unmounts at that point and the result never lands. The host owns
	 * this signal because the machine registry is product-local.
	 */
	hasLiveMachineFlow?: boolean;
	/** See `EditorRemixContext.onVariantUpdateComplete`. */
	onVariantUpdateComplete?: (id: string) => void;
	/** See `EditorRemixContext.onSubtypeMismatch`. */
	onSubtypeMismatch?: () => void;
	clearThinkingDecoration?: () => void;
	replaceWhiteboardEmbed?: WhiteboardEmbedReplacementCallback;
	setMauiEmbedFinalizeStatus?: (options: {
		contentId: string;
		status: 'suppressed' | 'needs-finalize';
	}) => void;
	editorApi?: TEditorApi;
	handleModalClose: () => void;
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	/** Whether to show the Labs badge in the Remix chooser. Defaults to visible. */
	showLabsLozenge?: boolean;
	isComplete?: boolean;
	isModalOpen: boolean;
	isThinking: boolean;
	remixAttemptId?: string;
	isUpdate?: boolean;
	isViewMode?: boolean;
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	dynamicUiSubtypeForUpdate?: string;
	showLocationPicker?: boolean;
	setThinkingComplete?: (isComplete: boolean) => void;
	/** Updates the editor-owned thinking bar while Remix is awaiting disambiguation. */
	setThinkingWaitingForInput?: (isWaitingForInput: boolean) => void;
	finishRemixThinking?: () => void;
	onRemixThinkingCompleteAnimationFinished?: () => void;
	onStartThinking: (options?: { contentType?: string }) => void;
	/**
	 * `endsFlow` marks a dismissal the flow will not come back from, letting the host end the
	 * thinking presentation too. Leave it unset for a transient dismissal, such as a refused
	 * admission, where that would tear down the live flow's bar.
	 */
	onDismissThinkingUi?: (options?: { endsFlow?: boolean; reopenModal?: boolean }) => void;
	/**
	 * `preserveRequest` suppresses only the abort of the in-flight AI request; the host still
	 * dismisses the legacy thinking UI. Used when a preview opens while generation may still be
	 * streaming into it. Hosts that do not understand the option ignore it.
	 */
	onStopThinking: (options?: { reopenModal?: boolean; preserveRequest?: boolean }) => void;
	onRegisterAbortRequest?: (abortFn: () => void) => () => void;
	/** Registers product-owned lifecycle projection once this machine session is retained. */
	onRegisterEditorRemixMachineLifecycleOwnership?: (
		session: EditorRemixMachineLifecycleSession,
	) => EditorRemixMachineLifecycleOwnershipCleanup;
	/**
	 * Publishes this component's UX closures so command handlers mounted above it can drive them.
	 * Returns the registration's cleanup.
	 */
	onRegisterEditorRemixUxHandles?: (handles: EditorRemixUxHandles) => () => void;
	/**
	 * Resolved by the editor owning surface after it has established Remix availability, adapter
	 * capability, master-gate, and experiment eligibility. The Platform creator consumes this
	 * decision rather than independently evaluating rollout controls for each affordance.
	 */
	isEditorRemixStateRearchitectureTreatment?: boolean;
	/**
	 * Registers the token-fenced cancellation path used when ProseMirror mutates the active Remix
	 * source or update target. Returns the registration cleanup.
	 */
	onRegisterEditorRemixMutationCancellationSink?: (sink: () => boolean) => () => void;
	/** Disarms source/target mutation cancellation immediately before Remix writes its own result. */
	onBeforeEditorRemixResultInsertion?: () => void;
	setShowLocationPicker?: (showLocationPicker: boolean) => void;
	/** Records a Remix result for retention observation and optional delayed acceptance analytics. */
	registerRemixResultTracking?: RegisterRemixResultTracking;
	/**
	 * Product-supplied recovery handler invoked when automatic whiteboard
	 * finalization fails. Threaded into {@link RemixProductIntegration} by the
	 * product wrapper. See {@link RemixProductIntegration.onFinalizeFailed}.
	 */
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;
	remixModalMetadata?: RemixModalMetadataLike;
	remixSource?: string;
	selectedContext: SelectionContext;
};

/**
 * The product-owned editor Remix implementation selected for this editor instance.
 *
 * The capability is part of the creator contract so consumers do not need to know
 * which product gate or implementation selected the component.
 */
export type RemixContentComponentDefinition = {
	Component: ComponentType<RemixContentComponentProps>;
	/** Returns whether the selected fragment has content that the product can Remix. */
	isSelectionRemixable?: (fragment: JSONNode[]) => boolean;
	/** Lifecycle registration host that remains mounted while presentation is idle. */
	LifecycleHost?: ComponentType<EditorRemixLifecycleAdapterHostProps>;
	capabilities: {
		editorRemixLifecycleAdapter: boolean;
	};
};

export type EditorRemixLifecycleAdapterHostProps = Pick<
	RemixContentComponentProps,
	'onRegisterEditorRemixMachineLifecycleOwnership'
> & {
	onRegisterEditorInlineRovoEditLifecycleAdapter: (
		adapter: EditorRemixLifecycleAdapter,
	) => () => void;
};

export type RemixContentComponentFullProps<
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
> = RemixContentComponentProps<TEditorApi> & {
	editorRemixLifecycleAdapter?: EditorRemixLifecycleAdapter;
	entryPoint?: string;
	productConfig: RemixProductConfig;
	productIntegration: RemixProductIntegration;
	smartCreationConfig?: Pick<ConversationAssistantConfig, 'siteId' | 'userId' | 'product'>;
	smartCreationContext?: SmartCreationContext;
};

/**
 * Classified reason an external preview's editor insertion produced no positive acknowledgement.
 * Structurally identical to the Smart Creation capability's result; kept local on purpose so the
 * lifecycle does not depend on conversation-assistant types.
 */
export type ExternalPreviewInsertionNotAcknowledged = {
	insertionKind: 'embed' | 'image';
	outcome: 'rejected' | 'timed-out-cancelled' | 'timed-out-in-flight';
};

export type ExternalPreviewInsertionAcknowledgement = {
	onAcknowledged: () => void;
	onNotAcknowledged?: (result: ExternalPreviewInsertionNotAcknowledged) => void;
};

export type OpenRemixPreviewOptions = {
	isFloating: boolean;
	remixConversationId?: string;
	/**
	 * Invariant: invoke only after the preview surface actually opened (e.g. the staging modal's
	 * `onOpenCallback`), never when opening was merely requested. Lifecycle ownership settles on it.
	 * Integrations without a real open signal must leave it uncalled; the caller bounds the wait.
	 */
	onOpen?: () => void;
	onSubmit?: () => void;
	/**
	 * Present only when a retained lifecycle observes the external preview's editor insertion.
	 * `onAcknowledged` fires once after the finalize surface positively acknowledges durable insertion.
	 * `onNotAcknowledged` is diagnostics only: it reports the classified reason an attempt got no
	 * positive acknowledgement and never settles ownership.
	 */
	editorInsertionAcknowledgement?: ExternalPreviewInsertionAcknowledgement;
	onDismiss?: () => void;
};

export type RemixInsertSuccessPayload = {
	currentConversationId?: string;
	remixMetadata?: RemixFeedbackMetadata;
	requestId?: string;
};

export type RemixFinalizeFailedPayload = {
	/** The remixed whiteboard's content id (parsed from the embed URL). */
	contentId: string;
	/** The `~ROVO…` embed URL currently present in the document. */
	originalUrl: string;
};

export type RemixFinalizeSuccessPayload = {
	/** The remixed whiteboard's content id. */
	contentId: string;
	/** The parent used when the whiteboard was finalized. Root-level content has no parent. */
	parentId: string | undefined;
};

/**
 * Terminal result of a bounded conversation-assistant handoff. Only `acknowledged` proves the
 * requested conversation is showing; every other outcome is a distinct reason no proof arrived.
 * Negative outcomes release shadow ownership as an unacknowledged handoff (`end('abandoned')`)
 * and nothing else: no `handed-off`, no `CANCELLED`, no producer abort, no product failure, and no
 * cancellation UX.
 */
export type HandoffAcknowledgementResult =
	| { outcome: 'acknowledged' }
	| { outcome: 'timed-out' }
	| { outcome: 'rejected' }
	| { outcome: 'abandoned'; reason: 'superseded' | 'unmounted' };

export type RemixProductIntegration = {
	closeConversationAssistant?: () => void;
	closeRemixPreview: () => void;
	contentType: string;
	sourceContent?: {
		contentId?: string;
		contentType?: string;
	};
	isRemixPreviewVisible: boolean;
	onRemixInsertSuccess?: (payload: RemixInsertSuccessPayload) => void;
	/**
	 * Fired after automatic finalization moves a remixed whiteboard into the
	 * product space.
	 */
	onFinalizeSuccess?: (payload: RemixFinalizeSuccessPayload) => void;
	/**
	 * Fired when automatic finalization of a remixed whiteboard fails. Product
	 * integrations may take over the recovery UX (e.g. open a space picker) and
	 * return `true` to signal the failure was handled.
	 */
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;
	/**
	 * Notifies the product that Remix is waiting for a user response. The product owns deciding
	 * which surface should make that request visible.
	 */
	onRemixInputRequired?: (conversationId?: string) => void;
	openRemixPreview: (options: OpenRemixPreviewOptions) => void;
	openConversationAssistant?: (location: string, conversationId?: string) => void;
	/**
	 * Resolves `acknowledged` only after the requested conversation-assistant surface is observed
	 * showing the requested conversation. Never rejects for an expected negative outcome.
	 */
	requestConversationAssistantOpen?: (
		location: string,
		conversationId?: string,
	) => Promise<HandoffAcknowledgementResult>;
	toggleConversationAssistant?: (location: string) => void;
};

export type RemixProductConfig = {
	analyticsProductName?: string;
	getRemixGeneratePreviewMetadata?: (
		selection: RemixOptionSelectionParameters,
	) => Record<string, boolean | number | string | undefined>;
	isActionReady?: (chatMessages: StoreChatItem[]) => boolean;
	isNodeUnsupportedForRemix?: (node: ADFEntity) => boolean;
	productKey: string;
	shouldUseEphemeralPreview?: (chatMessages: StoreChatItem[]) => boolean;
};
